import Config from "../_config/config";
import myAxios from "./axiositeachWrapper";

/**
 * Shared CKEditor 5 image-upload adapter for the CRM rich-text editors
 * (campaign send, drip sequence step, company bulk-email, conversation reply).
 *
 * Uses the existing JWT-attaching `myAxios` so we don't have to plumb tokens
 * into CKEditor's built-in SimpleUploadAdapter.
 *
 * Posts the chosen image to POST /v1/campaigns/upload-image (multipart) and
 * expects { url } back. Validates type + size client-side too for fast feedback.
 *
 * Usage in a CKEditor config:
 *
 *   <CKEditor
 *     editor={ClassicEditor}
 *     config={{
 *       toolbar: [..., "imageUpload"],   // adds the toolbar button
 *       extraPlugins: [imageUploadPlugin],
 *       ...
 *     }}
 *   />
 */

const ALLOWED_MIMES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

class CrmImageUploadAdapter {
  private loader: any;
  private aborted = false;

  constructor(loader: any) {
    this.loader = loader;
  }

  upload(): Promise<{ default: string }> {
    return this.loader.file.then(
      (file: File) =>
        new Promise((resolve, reject) => {
          if (this.aborted) {
            reject(new Error("Upload aborted"));
            return;
          }
          if (!ALLOWED_MIMES.includes(file.type)) {
            reject(`Unsupported file type ${file.type}. Use JPEG, PNG, GIF or WebP.`);
            return;
          }
          if (file.size > MAX_BYTES) {
            reject(`Image too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 5MB.`);
            return;
          }

          const fd = new FormData();
          fd.append("file", file);

          // Do NOT set Content-Type — axios + browser must set it with the
          // multipart boundary, otherwise multer can't parse req.file. The
          // JWT is attached by the myAxios request interceptor.
          myAxios
            .post<any>(`${Config.nodeSiteUrl}v1/campaigns/upload-image`, fd)
            .then((res) => {
              const url: string | undefined = res.data?.url;
              if (!url) {
                reject(res.data?.error?.message || "Upload succeeded but no URL was returned");
                return;
              }
              resolve({ default: url });
            })
            .catch((err) => {
              const status = err?.response?.status;
              const serverMsg = err?.response?.data?.error?.message;
              if (status === 401 || status === 403) {
                reject("Your session has expired. Please log in again to upload images.");
                return;
              }
              reject(serverMsg || err?.message || "Upload failed");
            });
        }),
    );
  }

  abort(): void {
    this.aborted = true;
  }
}

/**
 * Plugin factory — pass this into CKEditor's `extraPlugins` array.
 *
 * Also intercepts CKEditor's Notification system so upload errors are routed
 * to the console (and an optional window-level callback consumers can wire to
 * a toast) instead of falling back to `window.alert()`, which is what users
 * were seeing for auth failures from this endpoint.
 */
export function imageUploadPlugin(editor: any): void {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) =>
    new CrmImageUploadAdapter(loader);

  try {
    const notifications = editor.plugins.get("Notification");
    if (notifications) {
      notifications.on(
        "show:warning",
        (evt: any, data: any) => {
          // Stop CKEditor's default handler, which calls window.alert().
          evt.stop();
          const msg = data?.message || "Image action failed";
          // eslint-disable-next-line no-console
          console.warn("[CKEditor upload]", msg);
          const handler = (window as any).__ckeditorUploadErrorHandler;
          if (typeof handler === "function") {
            try { handler(msg); } catch { /* ignore */ }
          }
        },
        { priority: "high" },
      );
    }
  } catch {
    // Notification plugin not present — nothing to suppress.
  }
}

/** Toolbar entries that expose the image-upload button + edit options. */
export const imageToolbarItems = ["imageUpload", "|", "imageStyle:full", "imageStyle:side", "imageTextAlternative"];
